
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

const TERMS_HTML = `
<h3>1. Introduction</h3>
<p>These Terms of Service ("Terms") govern your access to and use of the Outlne website, platform, and interior design visualization services (collectively, the "Service"), provided by Outlne, Inc. ("Outlne," "we," "us," or "our"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.</p>

<h3>2. Eligibility and Account</h3>
<p><strong>2.1.</strong> You must be at least 18 years old to create an account. By signing up, you represent and warrant that you meet this age requirement. Any misrepresentation of your age or birthdate is a material breach of these Terms.</p>
<p><strong>2.2.</strong> You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account. You agree to notify us immediately of any unauthorized use.</p>
<p><strong>2.3.</strong> We reserve the right to suspend or terminate accounts that violate these Terms or pose a risk to Outlne or other users.</p>

<h3>3. Service Description</h3>
<p>Outlne is an interior design visualization platform that allows Users to upload a photo of any room and make edits through manual area selections or quick style transformations. Users simply snap a photo, draw/select what they want to change, and instantly preview new furniture, styles, and finishes in their actual space. The platform supports 3D models, architectural tools for measurements, floor plans, annotated exports, and assists in ultra‑realistic renders. Generated Content is produced through integrated generative processing technology for visualization planning purposes only. The Service is intended for residential and client commercial use, enabling Users to plan real‑world changes and present ideas to contractors, landlords, and/or condo boards before any physical modifications are made. The Service relies on third‑party processing providers and cloud infrastructure. Features may be added, modified, or discontinued at our discretion as set forth in Section 14.</p>

<h3>4. User Content and Intellectual Property</h3>
<p><strong>4.1. Your Content.</strong> You retain all ownership rights in the original images, designs, 3D models, textures, and other materials you upload ("User Content").</p>
<p><strong>4.2. License to Outlne.</strong> By uploading User Content, you grant Outlne a worldwide, non‑exclusive, royalty‑free, fully paid‑up license to host, store, reproduce, modify (solely as necessary to provide the Service, e.g., resizing, format conversion), and display your User Content. This license ends when you delete your User Content and your account, except as retained in backups for a reasonable period (up to 90 days).</p>
<p><strong>4.3. Photo/Video Permission.</strong> Before uploading any photo or video, you must confirm that you have the right to use it and accept full liability for any copyright or other claims. This confirmation forms part of your representations under Section 4.5.</p>
<p><strong>4.4. Generated Content.</strong> Subject to your compliance with these Terms and payment of applicable fees, Outlne grants you a non‑exclusive, perpetual, worldwide, royalty‑free license to use, reproduce, display, and create derivative works of the generative outputs ("Generated Content") for both personal and commercial purposes, including client projects, portfolios, and marketing materials. You are free to transfer ownership of Generated Content to your clients; Outlne does not mediate or manage such transfers.</p>
<p><strong>4.5. Prohibited Content.</strong> You may not upload User Content that:</p>
<ul>
  <li>a) Infringes any third‑party intellectual property, privacy, or publicity right;</li>
  <li>b) Is unlawful, harassing, defamatory, obscene, or promotes violence;</li>
  <li>c) Contains malicious code or viruses;</li>
  <li>d) Depicts minors in a harmful or inappropriate manner.</li>
</ul>
<p><strong>4.6. User Warranties.</strong> You represent and warrant that you own or have secured all necessary licenses, consents, and permissions for the User Content you upload, and that such content does not violate any law or third‑party rights. You will indemnify Outlne for any claims, damages, liabilities, and expenses (including reasonable attorneys' fees) arising from your breach of this warranty.</p>

<h3>5. Acceptable Use</h3>
<p>You agree not to:</p>
<ul>
  <li>Reverse engineer, decompile, or attempt to extract the source code of the Service;</li>
  <li>Use the Service to develop a competing product;</li>
  <li>Bypass any security measures or access non‑public areas of the Service;</li>
  <li>Use the Service to generate spam, disinformation, deepfakes intended to deceive, or any content that violates our Acceptable Use Policy;</li>
  <li>Resell, sublicense, or otherwise commercially exploit the Service itself (as opposed to Generated Content) without a separate written agreement with Outlne.</li>
</ul>

<h3>6. Payments, Subscriptions, and Refunds</h3>
<p><strong>6.1. Fees.</strong> Certain features require paid subscriptions. All fees are non‑refundable except as required by applicable law.</p>
<p><strong>6.2. Plans.</strong> Our subscription plans and their recurring fees are published on our website. As of the Last Updated date, the plans include:</p>
<ul>
  <li><strong>Trial:</strong> free, limited generation budget (~100 iterations or 2–3 videos, combined pool). Once exhausted, read‑only access — you may view/delete projects but not generate or export.</li>
  <li><strong>Creator:</strong> $45/month, includes a base monthly generation budget of 342 iterations OR 12 videos (combined pool). Top‑up available up to $100 per billing cycle.</li>
  <li><strong>Pro:</strong> $100/month, includes a base monthly generation budget of 765 iterations OR 27 videos (combined pool). Top‑up available up to $1,000 per billing cycle.</li>
</ul>
<p><strong>6.3. Generation Budget Mechanics.</strong> One combined pool of iterations and videos. When exhausted, you may upgrade your plan or add balance (top‑up). Base monthly allotments do not roll over; top‑up balances roll over indefinitely. Top‑up balances are non‑refundable, as the corresponding processing capacity is loaded and reserved on third‑party infrastructure immediately upon purchase, incurring non‑recoverable costs.</p>
<p><strong>6.4. Billing.</strong> Subscription fees are billed in advance on a recurring basis (monthly or annually as applicable) and will auto‑renew until canceled. You may cancel at any time through account settings; cancellation takes effect at the end of the current billing period. No refunds for partial periods.</p>
<p><strong>6.5. Price Changes.</strong> We may change fees upon 30 days' notice. Continued use after the change constitutes acceptance.</p>

<h3>7. Third‑Party Services and Processing</h3>
<p>The Service integrates third‑party processing APIs, cloud hosting, image providers (including Unsplash), and payment processors. Outlne is not responsible for the availability or output of these third‑party services. Your use of Generated Content may be subject to additional terms of the underlying processing model licenses. You agree to comply with all applicable third‑party terms. Outlne does not guarantee that processing providers will not use your content for model improvement; we select vendors based on privacy protectiveness but have not obtained contractual no‑use assurances from all providers. See our Privacy Policy and Technology & Data Processing Note.</p>

<h3>8. Privacy</h3>
<p>Our collection and use of personal information is governed by our Privacy Policy, which is incorporated by reference.</p>

<h3>9. Disclaimer of Warranties</h3>
<p><strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON‑INFRINGEMENT. OUTLNE DOES NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR‑FREE, OR SECURE, NOR THAT GENERATED CONTENT WILL MEET YOUR REQUIREMENTS OR BE ACCURATE.</strong></p>

<h3>10. Limitation of Liability</h3>
<p><strong>TO THE FULLEST EXTENT PERMITTED BY LAW, OUTLNE, ITS AFFILIATES, OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. IN ANY EVENT, OUTLNE'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM. THE FOREGOING LIMITATIONS APPLY EVEN IF THE REMEDY FAILS OF ITS ESSENTIAL PURPOSE.</strong></p>

<h3>11. Indemnification</h3>
<p>You agree to indemnify, defend, and hold harmless Outlne from any claims, damages, liabilities, and expenses (including reasonable attorneys' fees) arising out of: (a) your User Content; (b) your use of the Service in violation of these Terms; (c) your violation of any third‑party right; or (d) your failure to obtain necessary approvals before implementing any design in the real world.</p>

<h3>12. Termination</h3>
<p>We may terminate or suspend your account at any time, with or without cause, upon notice. Upon termination, your right to use the Service ceases immediately. Provisions that by their nature should survive termination (e.g., IP, disclaimers, indemnification) shall survive.</p>

<h3>13. Governing Law and Disputes</h3>
<p>These Terms shall be governed by the laws of the State of California, without regard to conflict‑of‑law principles. Any dispute shall be resolved exclusively in the state or federal courts located in Los Angeles County, California. You waive any objection to personal jurisdiction and venue.</p>

<h3>14. Changes to Terms</h3>
<p>We may modify these Terms at any time. We will notify you of material changes via email or through the Service. Your continued use after the effective date constitutes acceptance of the revised Terms.</p>

<h3>15. Contact</h3>
<p>Email: legal@outlne.com</p>
`;

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Service | Outlne";
  }, []);

  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);

  const lastGridChangeTime = useRef(0);
  const bgIntervalRef = useRef(null);
  const bgAnimationRef = useRef(null);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1224;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      setWindowWidth(width);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Page load effect - fade out grid and fade in background
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Optimized background slideshow
  useEffect(() => {
    const preloadImages = () => {
      backgrounds.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };
    preloadImages();

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';
      
      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);
      
      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    if (pageLoaded) {
      changeBackground();
      bgIntervalRef.current = setInterval(changeBackground, 8000);
    }

    return () => {
      clearInterval(bgIntervalRef.current);
      if (bgAnimationRef.current) {
        cancelAnimationFrame(bgAnimationRef.current);
      }
    };
  }, [pageLoaded]);

  // Grid animation - only run after page has loaded
  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      
      const timeSinceChange = time - lastGridChangeTime.current;
      
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      
      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid((prev) => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // Grid styles
  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Background Slideshow */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        willChange: 'transform',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          transformStyle: 'preserve-3d',
        }} />
      </div>

      {/* Grid Container - Fades out after load */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, white 1px, transparent 1px),
                linear-gradient(to bottom, white 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* Desktop Header */}
      {!isMobile && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: '30px',
              left: '30px',
              zIndex: 20,
              cursor: 'pointer',
              fontSize: '2rem',
              color: 'white',
            }}
            onClick={() => navigate('/')}
          >
            ×
          </div>

          <div style={{
            position: 'fixed',
            top: '40px',
            right: '40px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src={LogoWhite} 
                alt="Outlne Logo"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <h1 style={{
                fontSize: '1.8rem',
                fontFamily: 'avenir-lt-pro, sans-serif',
                letterSpacing: '-1.2px',
                margin: 0,
                color: 'white',
                fontWeight: 500,
              }}>
                Outlne
              </h1>
            </div>
            <p style={{
              fontSize: '1rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>
        </>
      )}

      {/* Mobile Header with Blur Background */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '110px',
            backgroundColor: 'rgba(31, 31, 31, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 15,
          }} />
          
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 20,
            textAlign: 'left',
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              letterSpacing: '-1.2px',
              margin: 0,
              color: 'white',
              fontWeight: 500,
            }}>
              Outlne
            </h1>
            <p style={{
              fontSize: '0.9rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>

          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img 
              src={LogoWhite} 
              alt="Outlne Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div 
              style={{
                cursor: 'pointer',
                marginRight: '10px',
                fontSize: '1.8rem',
                color: 'white',
                marginTop: '-5px',
              }}
              onClick={() => navigate('/')}
            >
              ×
            </div>
          </div>
        </>
      )}

      {/* Main Content Container - Scrollable with Footer */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 5,
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 1s ease',
        overscrollBehavior: 'none'
      }}>
        
        {/* Content Wrapper */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          minHeight: '100vh',
          paddingTop: isMobile ? '130px' : '150px',
          paddingBottom: '0',
          position: 'relative',
          zIndex: 5,
        }}>
          
          {/* Scrollable Modal Container */}
          <div style={{
            width: isMobile ? '85vw' : isTablet ? '55vw' : '600px',
            height: isMobile ? 'auto' : isTablet ? 'auto' : 'auto',
            minHeight: isMobile ? 'auto' : '500px',
            border: '2px solid white',
            borderRadius: '40px',
            overflow: 'hidden',
            padding: isMobile ? '15px' : '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'rgba(34, 34, 34, 0.95)',
            marginBottom: '3rem',
          }}>
            
            {/* Terms Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: isMobile ? '1.5rem' : '2rem',
              padding: isMobile ? '0 10px' : '0 20px',
              flexShrink: 0
            }}>
              <h1 style={{
                fontFamily: 'avenir-lt-pro, sans-serif',
                fontSize: isMobile ? '1.4rem' : isTablet ? '1.6rem' : '1.8rem',
                fontWeight: '500',
                color: 'white',
                marginBottom: '0.5rem',
                lineHeight: '1.2',
                letterSpacing: '-0.5px'
              }}>
                Terms of Service
              </h1>
              <p style={{
                color: '#ccc',
                fontSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
                lineHeight: '1.4',
                fontFamily: 'avenir-lt-pro, sans-serif',
                margin: 0
              }}>
                Last Updated: August 11, 2026
              </p>
              <div style={{
                marginTop: '0.25rem',
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                color: '#40a3ff',
                fontFamily: 'avenir-lt-pro, sans-serif',
              }}>
                <a href="/policycenter" style={{ color: '#40a3ff', textDecoration: 'none' }}>
                  View Policy Center
                </a>
              </div>
            </div>

            {/* Scrollable Terms Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: isMobile ? '0 5px' : '0 10px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.3) transparent',
              backgroundColor: 'rgba(27, 27, 27, 0.9)',
              borderRadius: '12px',
              maxHeight: isMobile ? '400px' : '500px',
            }}>
              <style>
                {`
                  ::-webkit-scrollbar {
                    width: 6px;
                  }
                  ::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  ::-webkit-scrollbar-thumb {
                    background: rgba(255,255,255,0.3);
                    border-radius: 3px;
                  }
                  ::-webkit-scrollbar-thumb:hover {
                    background: rgba(255,255,255,0.5);
                  }
                `}
              </style>
              
              <div 
                className="policy-content"
                style={{
                  padding: isMobile ? '15px 5px 20px 5px' : '20px 10px 20px 10px',
                  color: '#ccc',
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  lineHeight: '1.6',
                }}
                dangerouslySetInnerHTML={{ __html: TERMS_HTML }}
              />
            </div>

            {/* Footer inside modal */}
            <div style={{
              textAlign: 'center',
              paddingTop: isMobile ? '1rem' : '1.5rem',
              marginTop: 'auto',
              flexShrink: 0,
              borderTop: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: 'white',
                  color: '#000',
                  border: 'none',
                  borderRadius: '25px',
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontWeight: '500',
                  fontSize: isMobile ? '0.9rem' : '0.95rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                Close
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            width: '100%',
            position: 'relative',
            zIndex: 5,
            backgroundColor: '#1a1a1a',
          }}>
            <Footer />
          </div>
        </div>
      </div>

      {/* CSS Styles */}
      <style>
        {`
          html {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
          }
          
          body {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            margin: 0;
            padding: 0;
            overscroll-behavior: none;
            -webkit-overflow-scrolling: touch;
          }
          
          #root {
            height: 100%;
            width: 100%;
            overflow: hidden;
            position: fixed;
          }
          
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }
          
          *:focus {
            outline: none;
          }
          
          div[style*="mask-image"] {
            pointer-events: none;
          }

          .policy-content h2 {
            color: #ffffff;
            font-size: 1.8rem;
            font-weight: 700;
            margin: 0 0 0.5rem 0;
            letter-spacing: -0.02em;
          }
          .policy-content h3 {
            color: #ffffff;
            font-size: 1.2rem;
            font-weight: 700;
            margin: 1.5rem 0 0.75rem 0;
            letter-spacing: -0.01em;
          }
          .policy-content p {
            color: #e0e0e0;
            font-size: 0.95rem;
            line-height: 1.8;
            margin: 0.75rem 0;
          }
          .policy-content .last-updated {
            color: #aaaaaa;
            font-size: 0.85rem;
            margin-bottom: 1.5rem;
          }
          .policy-content ul {
            margin: 0.5rem 0 0.75rem 0;
            padding-left: 1.5rem;
          }
          .policy-content li {
            color: #e0e0e0;
            font-size: 0.95rem;
            line-height: 1.8;
            margin-bottom: 0.35rem;
          }
          .policy-content strong {
            color: #ffffff;
            font-weight: 700;
          }
        `}
      </style>
    </>
  );
}
